import React, { useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import Image from "next/image";
import withRouter, { WithRouterProps } from "next/dist/client/with-router";
import Link from "next/link";

import { UserInterface } from "../../models/User";
import { isViewAsActive, logoutUser, stopViewAs } from "../../utils/auth";
import { LoadingSpinner } from "src/components";

const defaultRoute = "/shows";
const pages = [
  { title: "Shows", route: "/shows" },
  { title: "Allocations", route: "/allocations/my-allocations" },
  { title: "Allocations V2", route: "/allocations-v2/my-allocations" },
  { title: "Users", route: "/users" },
  { title: "Rules", route: "/rules" },
  /* { title: 'Boilerplate', route: '/boilerplate' }, 'Guarded'*/
];

interface ResponsiveAppBarProps extends WithRouterProps {
  showMenu?: boolean;
  user?: UserInterface | null;
}

const ResponsiveAppBar: React.FC<ResponsiveAppBarProps> = ({
  showMenu = true,
  router,
  user,
}) => {
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = useState<null | HTMLElement>(null);

  const isViewAs = isViewAsActive();

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleNavigation = (route: string) => {
    setAnchorElNav(null);
    router.push(route === defaultRoute ? "/" : route);
  };

  const handleLogin = () => {
    router.replace(`/login?redirectUrl=${router.asPath}`);
  };

  return (
    <AppBar className="responsiveAppBar" position="static">
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "row" }}>
            <Box sx={{ display: { xs: "flex", md: "none" } }}>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
              {showMenu && (
                <Menu
                  id="menu-appbar"
                  anchorEl={anchorElNav}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  open={Boolean(anchorElNav)}
                  onClose={handleCloseNavMenu}
                  sx={{
                    display: { xs: "block", md: "none" },
                  }}
                >
                  {pages.map((page) => (
                    <MenuItem
                      key={page.title}
                      onClick={() => handleNavigation(page.route)}
                    >
                      <Typography textAlign="center">{page.title}</Typography>
                    </MenuItem>
                  ))}
                </Menu>
              )}
            </Box>
            <Link href="/">
              <Image
                src="/images/logo-white.webp"
                width="143"
                height="70"
                alt="That's The Ticket logo"
                title="That's The Ticket"
              />
            </Link>
            <Box sx={{ flexGrow: 1, display: { xs: "none", md: "flex" } }}>
              {showMenu &&
                pages.map((page) => (
                  <Link href={page.route} key={page.title}>
                    <Button
                      className={page.title.toLocaleLowerCase() + "-navigation"}
                      sx={{ my: 2, color: "white", display: "block" }}
                    >
                      {page.title}
                    </Button>
                  </Link>
                ))}
            </Box>
          </Box>
          {!user ? (
            <LoadingSpinner color="inherit" />
          ) : (
            <>
              <Box data-cy="login-status" sx={{ flexGrow: 0 }}>
                {user?.email ? (
                  <Box sx={{ display: "flex" }}>
                    <Box
                      data-cy="navbar-username"
                      onClick={handleOpenUserMenu}
                      sx={{ p: 0, cursor: "pointer" }}
                      style={isViewAs ? { color: "orange" } : {}}
                    >{user?.firstName && user?.lastName ?
                      (isViewAs ? `Viewing as ` : ``) +
                      `${user?.firstName} ${user?.lastName}` :
                      `Guest User - ${user?.email}`}
                    </Box>
                    <Box sx={{ px: "10px" }}>|</Box>
                    {isViewAs ? (
                      <Box
                        onClick={() => stopViewAs()}
                        sx={{ p: 0, cursor: "pointer" }}
                      >
                        Stop view as
                      </Box>
                    ) : (
                      <Box
                        data-cy="login-status-with-user"
                        onClick={() => logoutUser()}
                        sx={{ p: 0, cursor: "pointer" }}
                      >
                        Log out
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Box
                    data-cy="login-status-no-user"
                    onClick={() => handleLogin()}
                    sx={{ p: 0, cursor: "pointer" }}
                  >
                    Login
                  </Box>
                )}
                <Menu
                  sx={{ mt: "45px" }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  <MenuItem key={0} onClick={handleCloseUserMenu}>
                    <Link href="/profile">Profile</Link>
                  </MenuItem>
                </Menu>
              </Box>
            </>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default withRouter(ResponsiveAppBar);
